import { CategoryOptionsListProps } from "../proctoringOptionsGrid";
import { useEffect, useMemo, useState } from "react";
import { produce, type Producer } from "immer";
import OptionBlock from "../optionBlock";
import { PROCTORING_OPTION_TEXT } from "../option-text/ProctoringOptionText";
import i18nPackage from "../../../i18n/i18n";
import { CheckBoxMenuSubItem } from "../option-menus/checkBoxMenuSubItem";
import { useProctoringOptionContext } from "../../context/EditQuizContext";
import { useProctoringHelpers } from "../hooks/useProctoringHelpers";
import { Trans } from "react-i18next";
import FlaggedKeywordsList, { FlaggedKeywordWithInstituteLevel } from "../option-lists/FlaggedKeywordsList";
import { FlaggedKeyword } from "@verity/database/prisma";

function RecordVideoOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();
	const { isOptionAllowed, getPartiallySupportedPlatforms } = useProctoringHelpers();
	const { videoRecordingMode, faceTrackingMode, objectDetectionMode, secondaryVideoRecordingMode } = proctoringOption;
	const [bypassWasChecked, setBypassWasChecked] = useState<boolean>(videoRecordingMode !== "strict");

	return (
		<>
			<OptionBlock
				optionKey="videoRecordingMode"
				checked={videoRecordingMode !== "off"}
				updateState={(checked) =>
					setProctoringOption({
						...proctoringOption,
						videoRecordingMode: checked ? (bypassWasChecked ? "normal" : "strict") : "off",
						faceTrackingMode: checked ? faceTrackingMode : "off",
						objectDetectionMode: checked ? objectDetectionMode : "off",
					})
				}
				disabledMessage={i18nPackage.t("editQuizPage.configurationStep.instituteDisabledVideo")}
				menuItems={
					<>
						{isOptionAllowed("videoRecordingMode", "normal") && isOptionAllowed("videoRecordingMode", "strict") && (
							<CheckBoxMenuSubItem
								label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
								checked={bypassWasChecked}
								onChange={(checked) => {
									setProctoringOption({
										...proctoringOption,
										videoRecordingMode: checked ? "normal" : "strict",
									});
									setBypassWasChecked(checked);
								}}
							/>
						)}
						{isOptionAllowed("objectDetectionMode") && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={"editQuizPage.configurationStep.recordOptionsTSX.videoRecordingMode.objectDetectionMode"}
										components={{ b: <strong /> }}
									/>
								}
								checked={videoRecordingMode !== "off" && objectDetectionMode !== "off"}
								onChange={(checked) =>
									setProctoringOption({
										...proctoringOption,
										objectDetectionMode: checked ? "normal" : "off",
									})
								}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("objectDetectionMode")}
							/>
						)}
						{isOptionAllowed("faceTrackingMode") && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={"editQuizPage.configurationStep.recordOptionsTSX.videoRecordingMode.faceTrackingMode"}
										components={{ b: <strong /> }}
									/>
								}
								checked={videoRecordingMode !== "off" && faceTrackingMode !== "off"}
								onChange={(checked) =>
									setProctoringOption({
										...proctoringOption,
										faceTrackingMode: checked ? "normal" : "off",
									})
								}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("faceTrackingMode")}
							>
								{isOptionAllowed("faceTrackingMode", "normal") &&
									isOptionAllowed("faceTrackingMode", "strict") &&
									videoRecordingMode !== "off" &&
									faceTrackingMode !== "off" && (
										<CheckBoxMenuSubItem
											label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
											checked={faceTrackingMode === "normal"}
											onChange={(checked) => {
												setProctoringOption({
													...proctoringOption,
													faceTrackingMode: checked ? "normal" : "strict",
												});
											}}
										/>
									)}
							</CheckBoxMenuSubItem>
						)}
						{isOptionAllowed("secondaryVideoRecordingMode") && (
							<CheckBoxMenuSubItem
								label={
									<Trans
										i18n={i18nPackage}
										i18nKey={
											"editQuizPage.configurationStep.recordOptionsTSX.videoRecordingMode.secondaryVideoRecordingMode"
										}
										components={{ b: <strong /> }}
									/>
								}
								checked={videoRecordingMode !== "off" && secondaryVideoRecordingMode !== "off"}
								onChange={(checked) => {
									setProctoringOption({
										...proctoringOption,
										secondaryVideoRecordingMode: checked ? "normal" : "off",
									});
								}}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("secondaryVideoRecordingMode")}
							/>
						)}
					</>
				}
			/>
		</>
	);
}

function RecordAudioOption({
	instituteKeywords,
}: {
	instituteKeywords: CategoryOptionsListProps["data"]["instituteKeywords"];
}) {
	const { proctoringOption, setProctoringOption, isMobileLayout } = useProctoringOptionContext();
	const { getPartiallySupportedPlatforms, isOptionAllowed } = useProctoringHelpers();

	// pure states derived from singular option values
	const [internalOptionState, setInternalOptionState] = useState({
		isAudioRecordingOn: proctoringOption.audioRecordingMode !== "off",
		isBypassModeOn: proctoringOption.audioRecordingMode !== "strict",
		isQuietEnvironmentOn: Boolean(proctoringOption.quietEnvironment),
		isAudioTranscriptionOn: Boolean(proctoringOption.audioTranscription),
		isFlaggedKeywordsOn: proctoringOption.flaggedKeywords !== null,
		flaggedKeywordData: proctoringOption.flaggedKeywords,
		shouldFocusKeywords: false,
	});

	const [currentOptionKeywords, setCurrentOptionKeywords] = useState<FlaggedKeyword[]>(
		(internalOptionState.flaggedKeywordData ?? []).filter(
			(savedKw) => !instituteKeywords.some((instKw) => instKw.keyword === savedKw.keyword),
		),
	);

	// compute derived state as a sideeffect
	const updateInternalState = (recipe: Producer<typeof internalOptionState>) => {
		setInternalOptionState((prev) => produce(prev, recipe));
	};

	// Moved setProctoringOption to useEffect because react was complaining
	useEffect(() => {
		const {
			isAudioRecordingOn,
			isBypassModeOn,
			isQuietEnvironmentOn,
			isAudioTranscriptionOn,
			isFlaggedKeywordsOn,
			flaggedKeywordData,
		} = internalOptionState;

		setProctoringOption(
			produce(proctoringOption, (draft) => {
				draft.audioRecordingMode = isAudioRecordingOn ? (isBypassModeOn ? "normal" : "strict") : "off";
				draft.quietEnvironment = isAudioRecordingOn && isQuietEnvironmentOn;
				draft.audioTranscription = isAudioRecordingOn && isAudioTranscriptionOn;
				draft.flaggedKeywords =
					isAudioRecordingOn && isAudioTranscriptionOn && isFlaggedKeywordsOn ? flaggedKeywordData : null;
			}),
		);

		// Only re-sync when the internal UI state changes
	}, [internalOptionState, setProctoringOption]);

	const displayKeywords = useMemo((): FlaggedKeywordWithInstituteLevel[] => {
		const markedKeywords = (instituteKeywords ?? []).map((kw) => ({ ...kw, isInstituteLevel: true }));

		return [...currentOptionKeywords, ...markedKeywords];
	}, [instituteKeywords, currentOptionKeywords]);

	const handleKeywordChange = (keywords: FlaggedKeywordWithInstituteLevel[]) => {
		const editableKeywords = keywords.filter((kw) => !kw.isInstituteLevel);
		setCurrentOptionKeywords(editableKeywords);

		updateInternalState((draft) => {
			draft.flaggedKeywordData = editableKeywords;
		});
	};

	return (
		<OptionBlock
			checked={internalOptionState.isAudioRecordingOn}
			updateState={(checked) =>
				updateInternalState((draft) => {
					draft.isAudioRecordingOn = checked;
				})
			}
			optionKey="audioRecordingMode"
			disabledMessage={i18nPackage.t("editQuizPage.configurationStep.instituteDisabledAudio")}
			menuItems={
				<>
					{isOptionAllowed("audioRecordingMode", "normal") && isOptionAllowed("audioRecordingMode", "strict") && (
						<CheckBoxMenuSubItem
							label={i18nPackage.t("editQuizPage.proctoringOptionsTextTSX.bypassModeText")}
							checked={internalOptionState.isBypassModeOn}
							onChange={(checked) =>
								updateInternalState((draft) => {
									draft.isBypassModeOn = checked;
								})
							}
							partiallySupportedPlatforms={getPartiallySupportedPlatforms("audioRecordingMode", "strict")}
						/>
					)}
					<CheckBoxMenuSubItem
						label={
							<Trans
								i18n={i18nPackage}
								i18nKey={"editQuizPage.configurationStep.recordOptionsTSX.quietEnviornment"}
								components={{ b: <strong /> }}
							/>
						}
						checked={internalOptionState.isQuietEnvironmentOn}
						onChange={(checked) =>
							updateInternalState((draft) => {
								draft.isQuietEnvironmentOn = checked;
							})
						}
						partiallySupportedPlatforms={getPartiallySupportedPlatforms("quietEnvironment")}
					/>
					{isOptionAllowed("audioTranscription") && (
						<>
							<CheckBoxMenuSubItem
								label={PROCTORING_OPTION_TEXT.audioTranscription.description}
								checked={internalOptionState.isAudioTranscriptionOn}
								onChange={(checked) =>
									updateInternalState((draft) => {
										draft.isAudioTranscriptionOn = checked;
									})
								}
								partiallySupportedPlatforms={getPartiallySupportedPlatforms("audioTranscription")}
							>
								{isOptionAllowed("flaggedKeywords") && internalOptionState.isAudioTranscriptionOn && (
									<CheckBoxMenuSubItem
										label={PROCTORING_OPTION_TEXT.flaggedKeywords.description}
										checked={internalOptionState.isFlaggedKeywordsOn}
										onChange={(checked) =>
											updateInternalState((draft) => {
												draft.isFlaggedKeywordsOn = checked;
												draft.shouldFocusKeywords = checked;
											})
										}
									/>
								)}
							</CheckBoxMenuSubItem>
						</>
					)}
				</>
			}
		>
			{internalOptionState.isAudioRecordingOn && internalOptionState.isFlaggedKeywordsOn && (
				<FlaggedKeywordsList
					viewingContext="quiz"
					keywords={displayKeywords}
					onSettingChange={handleKeywordChange}
					focusOnMount={internalOptionState.shouldFocusKeywords && !isMobileLayout}
					onFocus={() =>
						updateInternalState((draft) => {
							draft.shouldFocusKeywords = false;
						})
					}
				/>
			)}
		</OptionBlock>
	);
}

function RecordScreenOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const recordScreen = proctoringOption.recordScreen;

	return (
		<OptionBlock
			optionKey="recordScreen"
			checked={Boolean(recordScreen)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					recordScreen: checked,
				})
			}
			disabledMessage={i18nPackage.t("editQuizPage.configurationStep.instituteDisabledScreen")}
		/>
	);
}

function RoomScanOption() {
	const { proctoringOption, setProctoringOption } = useProctoringOptionContext();

	const scanRoom = proctoringOption.scanRoom;

	return (
		<OptionBlock
			optionKey="scanRoom"
			checked={Boolean(scanRoom)}
			updateState={(checked) =>
				setProctoringOption({
					...proctoringOption,
					scanRoom: checked,
				})
			}
			disabledMessage={i18nPackage.t("editQuizPage.configurationStep.instituteDisabledRoom")}
		/>
	);
}

export default function RecordOptions({ data }: CategoryOptionsListProps) {
	const { shouldShowOption } = useProctoringHelpers();

	return (
		<>
			{shouldShowOption("videoRecordingMode") && <RecordVideoOption />}
			{shouldShowOption("audioRecordingMode") && <RecordAudioOption instituteKeywords={data.instituteKeywords} />}
			{shouldShowOption("recordScreen") && <RecordScreenOption />}
			{shouldShowOption("scanRoom") && <RoomScanOption />}
		</>
	);
}
